import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

const ACCENTS = ['#00d4aa', '#a78bfa', '#fbbf24', '#38bdf8', '#f472b6'];

export default function SavingsGoals({ defaultCollapsed, refreshKey, onRefresh }) {
  const [goals, setGoals] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', targetAmount: '', targetDate: '', currentAmount: '' });
  const [saving, setSaving] = useState(false);
  const [contributingId, setContributingId] = useState(null);
  const [contributeInput, setContributeInput] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api.getSavingsGoals();
      setGoals(d);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const saveGoal = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.targetAmount) return;
    setSaving(true);
    try {
      await api.addSavingsGoal({
        name: form.name.trim(),
        targetAmount: parseFloat(form.targetAmount),
        targetDate: form.targetDate || null,
        currentAmount: parseFloat(form.currentAmount) || 0,
      });
      setForm({ name: '', targetAmount: '', targetDate: '', currentAmount: '' });
      setAdding(false);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteGoal = async (id) => {
    if (!window.confirm('Delete this savings goal?')) return;
    try {
      await api.deleteSavingsGoal(id);
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  const submitContribution = async (id) => {
    const delta = parseFloat(contributeInput);
    if (isNaN(delta) || delta === 0) { setContributingId(null); return; }
    try {
      await api.contributeSavingsGoal(id, delta);
      setContributingId(null);
      setContributeInput('');
      load();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  if (!goals) return null;

  return (
    <CollapsiblePanel
      title="🎯 Savings Goals"
      defaultCollapsed={defaultCollapsed}
      headerExtra={
        <button style={styles.addBtn} onClick={() => setAdding((a) => !a)}>
          {adding ? 'Cancel' : '+ New Goal'}
        </button>
      }
    >
      <p style={styles.helpText}>
        Save toward anything - a vacation, a new car, an emergency fund.
        Track progress and see if you're on pace to hit your date.
      </p>

      {adding && (
        <form onSubmit={saveGoal} style={styles.form}>
          <input
            style={styles.input}
            placeholder="Goal name (e.g. Summer Vacation)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div style={styles.formRow}>
            <input
              type="number"
              min="1"
              step="10"
              style={styles.input}
              placeholder="Target $"
              value={form.targetAmount}
              onChange={(e) => setForm({ ...form, targetAmount: e.target.value })}
            />
            <input
              type="number"
              min="0"
              step="10"
              style={styles.input}
              placeholder="Already saved (optional)"
              value={form.currentAmount}
              onChange={(e) => setForm({ ...form, currentAmount: e.target.value })}
            />
          </div>
          <input
            type="date"
            style={styles.input}
            value={form.targetDate}
            onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
          />
          <button style={styles.saveBtn} type="submit" disabled={saving || !form.name.trim() || !form.targetAmount}>
            {saving ? 'Saving...' : 'Create Goal'}
          </button>
        </form>
      )}

      {goals.length === 0 && !adding ? (
        <p style={styles.empty}>No savings goals yet - add one above.</p>
      ) : (
        <div style={styles.grid}>
          {goals.map((goal, i) => {
            const accent = ACCENTS[i % ACCENTS.length];
            const isContributing = contributingId === goal.id;

            return (
              <div key={goal.id} style={{ ...styles.card, borderColor: goal.isComplete ? '#00d4aa' : '#1e2a3a' }}>
                <div style={styles.cardHeader}>
                  <span style={styles.cardName}>
                    {goal.isComplete && '🎉 '}{goal.name}
                  </span>
                  <button style={styles.deleteBtn} onClick={() => deleteGoal(goal.id)}>✕</button>
                </div>

                <div style={styles.ringWrap}>
                  <svg width="88" height="88" viewBox="0 0 88 88">
                    <circle cx="44" cy="44" r="38" fill="none" stroke="#0a0f1e" strokeWidth="8" />
                    <circle
                      cx="44" cy="44" r="38" fill="none"
                      stroke={goal.isComplete ? '#00d4aa' : accent}
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={2 * Math.PI * 38}
                      strokeDashoffset={2 * Math.PI * 38 * (1 - goal.pct / 100)}
                      transform="rotate(-90 44 44)"
                      style={{ transition: 'stroke-dashoffset 0.6s ease', filter: `drop-shadow(0 0 4px ${accent}80)` }}
                    />
                  </svg>
                  <div style={styles.ringLabel}>{goal.pct.toFixed(0)}%</div>
                </div>

                <div style={styles.amounts}>
                  <span style={styles.currentAmt}>{fmt.currency(goal.currentAmount)}</span>
                  <span style={styles.targetAmt}> of {fmt.currency(goal.targetAmount)}</span>
                </div>

                {!goal.isComplete && goal.targetDate && (
                  <p style={styles.dateNote}>
                    {goal.daysRemaining > 0
                      ? `${goal.daysRemaining} days left · save ${fmt.currency(goal.requiredMonthly)}/mo to hit it`
                      : 'Target date has passed'}
                  </p>
                )}

                {!goal.isComplete && (
                  isContributing ? (
                    <div style={styles.contributeRow}>
                      <input
                        type="number"
                        step="10"
                        autoFocus
                        style={styles.contributeInput}
                        placeholder="+/- $"
                        value={contributeInput}
                        onChange={(e) => setContributeInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && submitContribution(goal.id)}
                      />
                      <button style={styles.contributeSaveBtn} onClick={() => submitContribution(goal.id)}>✓</button>
                      <button style={styles.contributeCancelBtn} onClick={() => { setContributingId(null); setContributeInput(''); }}>✕</button>
                    </div>
                  ) : (
                    <button style={{ ...styles.contributeBtn, borderColor: accent, color: accent }} onClick={() => setContributingId(goal.id)}>
                      + Add / Subtract
                    </button>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}
    </CollapsiblePanel>
  );
}

const styles = {
  addBtn: {
    background: 'transparent',
    border: '1px solid #00d4aa',
    color: '#00d4aa',
    borderRadius: '999px',
    padding: '4px 12px',
    fontSize: '0.72rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1rem',
  },
  form: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.6rem',
  },
  formRow: { display: 'flex', gap: '0.6rem' },
  input: {
    flex: 1,
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    color: '#fff',
    fontSize: '0.82rem',
    fontFamily: "'Syne', sans-serif",
    boxSizing: 'border-box',
  },
  saveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '8px',
    padding: '0.65rem',
    fontSize: '0.82rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: '0.85rem',
  },
  card: {
    background: '#0a0f1e',
    border: '1px solid',
    borderRadius: '14px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: '0.6rem',
  },
  cardName: {
    color: '#e5e7eb',
    fontSize: '0.8rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    textAlign: 'left',
  },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    color: '#374151',
    cursor: 'pointer',
    fontSize: '0.7rem',
    flexShrink: 0,
  },
  ringWrap: { position: 'relative', width: '88px', height: '88px', marginBottom: '0.6rem' },
  ringLabel: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  amounts: { marginBottom: '0.4rem' },
  currentAmt: {
    color: '#fff',
    fontSize: '1rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  targetAmt: {
    color: '#6b7280',
    fontSize: '0.75rem',
    fontFamily: "'DM Mono', monospace",
  },
  dateNote: {
    color: '#6b7280',
    fontSize: '0.65rem',
    lineHeight: '1.4',
    margin: '0 0 0.6rem',
  },
  contributeBtn: {
    background: 'transparent',
    border: '1px solid',
    borderRadius: '999px',
    padding: '4px 12px',
    fontSize: '0.7rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    marginTop: 'auto',
  },
  contributeRow: { display: 'flex', gap: '4px', marginTop: 'auto' },
  contributeInput: {
    width: '70px',
    background: '#111827',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '4px 6px',
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: "'DM Mono', monospace",
  },
  contributeSaveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '5px',
    width: '24px',
    cursor: 'pointer',
    fontWeight: '700',
  },
  contributeCancelBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '5px',
    width: '24px',
    cursor: 'pointer',
  },
};
